import type {
  BRMOrganizationBase,
  CompleteOrganizationInfo,
} from '@/types/external/2work/doctypes/CustomDoctypes'
import type { AddressDynamic } from '@/types/external/2work/doctypes/generatorOutput/Contacts/AddressDynamic'
import type { Contact } from '@/types/external/2work/doctypes/generatorOutput/Contacts/Contact'
import type { ContactEmailDynamic } from '@/types/external/2work/doctypes/generatorOutput/Contacts/ContactEmailDynamic'
import type { ContactPhoneDynamic } from '@/types/external/2work/doctypes/generatorOutput/Contacts/ContactPhoneDynamic'

const sortAddresses = (organization?: {
  name?: string
  tw_addresses?: AddressDynamic[] | null
  tw_primary_address?: AddressDynamic | null
}): ((current: AddressDynamic, next: AddressDynamic) => number) => {
  return (current: AddressDynamic, next: AddressDynamic): number => {
    const primarySortValue =
      Number(next.name === organization?.tw_primary_address?.name) -
      Number(current.name === organization?.tw_primary_address?.name)

    if (primarySortValue !== 0) {
      return primarySortValue
    }

    // sort by address link creation date

    //TODO: add address link to organization creation date from backend (e.g. tw_link_creation_date)

    const linkCreationNext = next.links?.find((link) => {
      return link.link_name === organization?.name
    })?.tw_created_at

    const linkCreationCurrent = current.links?.find((link) => {
      return link.link_name === organization?.name
    })?.tw_created_at

    if (linkCreationNext && linkCreationCurrent) {
      return new Date(linkCreationCurrent).getTime() - new Date(linkCreationNext).getTime()
    }

    return 0
  }
}

const isPrimaryContact = (compareContact: Contact, primaryContact?: Contact | null): boolean => {
  if (!primaryContact) {
    return false
  }

  return compareContact.name === primaryContact.name
}

const sortContacts = (organization: BRMOrganizationBase) => {
  return (current: Contact, next: Contact): number => {
    if (isPrimaryContact(current, organization.tw_primary_contact)) {
      return -1
    } else if (isPrimaryContact(next, organization.tw_primary_contact)) {
      return 1
    }
    const currentLink = current.links?.find((link) => {
      return link.link_name === organization.name
    })
    const nextLink = next.links?.find((link) => {
      return link.link_name === organization.name
    })

    if (!currentLink?.tw_created_at || !nextLink?.tw_created_at) {
      console.debug('COULD NOT FIND LINK')

      return 0
    }

    return (
      new Date(currentLink.tw_created_at).getTime() - new Date(nextLink.tw_created_at).getTime()
    )
  }
}

const sortEmails = (current: ContactEmailDynamic, next: ContactEmailDynamic): number => {
  if (current.is_primary !== undefined && next.is_primary !== undefined) {
    return next.is_primary - current.is_primary
  } else if (current.is_primary !== undefined) {
    return current.is_primary
  } else if (next.is_primary !== undefined) {
    return next.is_primary
  }

  // sort by email creation date
  if (current.creation !== undefined || next.creation !== undefined) {
    if (current.creation === undefined) {
      return 1
    } else if (next.creation === undefined) {
      return -1
    }

    const currentCreationDateTime = new Date(current.creation).getTime()
    const nextCreationDateTime = new Date(next.creation).getTime()

    return currentCreationDateTime - nextCreationDateTime
  }

  return 0
}

const sortOrganizations = (
  current: CompleteOrganizationInfo,
  next: CompleteOrganizationInfo,
): number => {
  if (next.link.tw_is_primary_link !== undefined && current.link.tw_is_primary_link !== undefined) {
    const primarySort = next.link.tw_is_primary_link - current.link.tw_is_primary_link

    if (primarySort !== 0) {
      return primarySort
    }

    // sort by organization link creation date
    if (next.link.tw_created_at && current.link.tw_created_at) {
      return (
        new Date(current.link.tw_created_at).getTime() - new Date(next.link.tw_created_at).getTime()
      )
    }
    if (next.link.tw_created_at) {
      return -1
    }
    if (current.link.tw_created_at) {
      return 1
    }

    return 0
  }
  if (next.link.tw_is_primary_link !== undefined) {
    return next.link.tw_is_primary_link
  }
  if (current.link.tw_is_primary_link !== undefined) {
    return current.link.tw_is_primary_link
  }

  return 0
}

const sortPhones = (current: ContactPhoneDynamic, next: ContactPhoneDynamic): number => {
  // TODO: also check "is_primary_mobile_no"?
  if (current.is_primary_phone !== undefined && next.is_primary_phone !== undefined) {
    return next.is_primary_phone - current.is_primary_phone
  } else if (current.is_primary_phone !== undefined) {
    return current.is_primary_phone
  } else if (next.is_primary_phone !== undefined) {
    return next.is_primary_phone
  }

  // sort by phone creation date
  if (current.creation !== undefined || next.creation !== undefined) {
    if (current.creation === undefined) {
      return 1
    } else if (next.creation === undefined) {
      return -1
    }

    const currentCreationDateTime = new Date(current.creation).getTime()
    const nextCreationDateTime = new Date(next.creation).getTime()

    return currentCreationDateTime - nextCreationDateTime
  }

  return 0
}

export { sortAddresses, sortContacts, sortEmails, sortOrganizations, sortPhones }
